import React, { useState, useEffect } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Box,
  VStack,
  SimpleGrid,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  Heading,
  Image,
  Spinner,
  Alert,
  AlertIcon,
  Checkbox,
  FormErrorMessage,
  Button,
  useToast,
  Text,
  Flex,
  HStack,
  Link as ChakraLink
} from '@chakra-ui/react';
import apiService from '../services/api';
import { FormConfig, Category, Component } from '../types';
import DOMPurify from 'dompurify';
import { useComponents } from '../hooks/useComponents';
import FieldSection from './FieldSection';
import logo from '../assets/images/logo-negative.svg';

const FormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [config, setConfig] = useState<FormConfig | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [componentValues, setComponentValues] = useState<Record<string, any>>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  
  // Standard form fields
  const [standardFields, setStandardFields] = useState({
    lastName: '',
    firstName: '',
    birthDate: '',
    insuranceCardNumber: '',
    phone: '',
    email: ''
  });
  
  const toast = useToast();

  // react-query: fetch components when category selected
  const {
    data: components = [],
    isLoading: loadingComponents,
    error: componentsError,
  } = useComponents(selectedCategory);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        if (!id) throw new Error('Form ID is missing');
        const data = await apiService.getFormConfig(id);
        setConfig(data);
      } catch (err: any) {
        setError(err.message || 'Failed to load form configuration');
      } finally {
        setLoading(false);
      }
    };
    fetchConfig();
  }, [id]);

  // Handle category change
  const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const categoryId = e.target.value;
    setSelectedCategory(categoryId);
  };

  // Handle value updates for dynamic components
  const handleComponentValueChange = (id: string, value: any) => {
    setComponentValues(prev => ({ ...prev, [id]: value }));
  };

  // Validation helper functions
  const validateEmail = (email: string): string | null => {
    if (!email.trim()) return 'E-Mail ist erforderlich';
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      return 'Bitte eine gültige E-Mail-Adresse eingeben (z.B. name@domain.ch)';
    }
    
    return null;
  };

  const validatePhone = (phone: string): string | null => {
    if (!phone.trim()) return 'Telefon ist erforderlich';
    
    const cleanPhone = phone.trim();
    
    // Allow international formats: +49 123 456789, 0123 456789, (0123) 456-789, etc.
    // Must contain at least 10 digits total
    // eslint-disable-next-line no-useless-escape
    const phoneRegex = /^[+]?[\d\s\-()\/]{10,25}$/;
    const digitCount = (cleanPhone.match(/\d/g) || []).length;
    
    if (!phoneRegex.test(cleanPhone)) {
      return 'Bitte eine gültige Telefonnummer eingeben';
    }
    
    if (digitCount < 10) {
      return 'Telefonnummer muss mindestens 10 Ziffern enthalten';
    }
    
    if (digitCount > 15) {
      return 'Telefonnummer darf höchstens 15 Ziffern enthalten';
    }
    
    return null;
  };

  // Handle value updates for standard form fields
  const handleStandardFieldChange = (field: keyof typeof standardFields, value: string) => {
    setStandardFields(prev => ({ ...prev, [field]: value }));
    
    // Clear field-specific error when user starts typing
    if (fieldErrors[field]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Helper to render each component based on type
  const renderComponent = (comp: Component) => {
    const { blu_componentid, blu_name, blu_componenttypecode, blu_richdescription, blu_inputtypecode } = comp;

    // Information/Text
    if (blu_componenttypecode === 758670001) {
      return (
        <Box
          key={blu_componentid}
          p={6}
          bg="#F8F8F8"
          my={6}
          sx={{
            fontFamily: `'ABC Favorit', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif`,
            fontSize: '1rem',
            color: '#1c2963',
            '& *': {
              fontFamily: `'ABC Favorit', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif !important`,
              color: '#1c2963 !important',
            },
          }}
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(blu_richdescription || '') }}
        />
      );
    }

    // Eingabefeld
    if (blu_componenttypecode === 758670000) {
      switch (blu_inputtypecode) {
        case 758670000: // Text
          return (
            <Box 
              key={blu_componentid} 
              py={3}
            >
              <FormControl isRequired={!!comp.blu_mendatorycode} isInvalid={!!fieldErrors[blu_componentid]}>
                <FormLabel mb={2} fontSize="0.9rem" fontWeight="600" color="#1c2963">
                  {blu_name}
                </FormLabel>
                <Input
                  type="text"
                  value={componentValues[blu_componentid] || ''}
                  onChange={(e) => handleComponentValueChange(blu_componentid, e.target.value)}
                  variant="flushed"
                  size="sm"
                  py={3}
                  bg="transparent"
                  sx={{
                    _placeholder: {
                      fontSize: '0.85rem',
                      fontWeight: 'bold',
                      color: '#aaa6ff',
                      opacity: 0.8,
                      fontStyle: 'italic'
                    },
                    borderBottomColor: '#1c2963',
                  }}
                />
                <FormErrorMessage mt={2} fontSize="sm" fontWeight="500">
                  {fieldErrors[blu_componentid]}
                </FormErrorMessage>
              </FormControl>
            </Box>
          );
        case 758670001: // Email
          return (
            <Box 
              key={blu_componentid} 
              py={3}
            >
              <FormControl isRequired={!!comp.blu_mendatorycode} isInvalid={!!fieldErrors[blu_componentid]}>
                <FormLabel mb={2} fontSize="0.9rem" fontWeight="600" color="#1c2963">
                  {blu_name}
                </FormLabel>
                <Input
                  type="email"
                  value={componentValues[blu_componentid] || ''}
                  onChange={(e) => handleComponentValueChange(blu_componentid, e.target.value)}
                  variant="flushed"
                  size="sm"
                  py={3}
                  bg="transparent"
                  sx={{
                    _placeholder: {
                      fontSize: '0.85rem',
                      fontWeight: 'bold',
                      color: '#aaa6ff',
                      opacity: 0.8,
                      fontStyle: 'italic'
                    },
                    borderBottomColor: '#1c2963',
                  }}
                />
                <FormErrorMessage mt={2} fontSize="sm" fontWeight="500">
                  {fieldErrors[blu_componentid]}
                </FormErrorMessage>
              </FormControl>
            </Box>
          );
        case 758670002: // Checkbox
          return (
            <Box 
              key={blu_componentid} 
              py={3}
            >
              <FormControl isRequired={!!comp.blu_mendatorycode} isInvalid={!!fieldErrors[blu_componentid]}>
                <Checkbox
                  isChecked={componentValues[blu_componentid] || false}
                  onChange={(e) => handleComponentValueChange(blu_componentid, e.target.checked)}
                  size="md"
                  alignItems="center"
                  sx={{
                    '.chakra-checkbox__control[data-checked]': {
                      bg: '#aaa6ff',
                      borderColor: '#aaa6ff',
                    },
                    '.chakra-checkbox__control:hover': {
                      borderColor: '#aaa6ff',
                    },
                  }}
                  borderColor="gray.300"
                >
                  <Box ml={3} display="block" maxW="1000px" width="100%">
                    <Text as="span" fontSize="0.9rem" fontWeight="600" color="#1c2963" wordBreak="break-word" whiteSpace="normal" display="inline">
                      {blu_name}
                      {comp.blu_mendatorycode && (
                        <Text as="span" color="red.500" fontSize="0.9rem" fontWeight="600" ml={1} verticalAlign="top">*</Text>
                      )}
                    </Text>
                  </Box>
                </Checkbox>
                <FormErrorMessage>
                  {fieldErrors[blu_componentid]}
                </FormErrorMessage>
              </FormControl>
            </Box>
          );
        case 758670003: // Auswahl (Select)
          const options = comp.blu_option2blu_component || [];
          return (
            <Box 
              key={blu_componentid} 
              py={3}
            >
              <FormControl isRequired={!!comp.blu_mendatorycode} isInvalid={!!fieldErrors[blu_componentid]}>
                <FormLabel mb={2} fontSize="0.9rem" fontWeight="600" color="#1c2963">
                  {blu_name}
                </FormLabel>
                <Select
                  value={componentValues[blu_componentid] || ''}
                  onChange={(e) => handleComponentValueChange(blu_componentid, e.target.value)}
                  variant="flushed"
                  size="sm"
                  py={3}
                  bg="transparent"
                >
                  <option value="">Bitte wählen</option>
                  {options.map((opt) => (
                    <option key={opt.blu_optionid} value={opt.blu_name}>{opt.blu_name}</option>
                  ))}
                </Select>
                <FormErrorMessage mt={2} fontSize="sm" fontWeight="500">
                  {fieldErrors[blu_componentid]}
                </FormErrorMessage>
              </FormControl>
            </Box>
          );
        case 758670004: // Datum
          return (
            <Box 
              key={blu_componentid} 
              py={3}
            >
              <FormControl isRequired={!!comp.blu_mendatorycode} isInvalid={!!fieldErrors[blu_componentid]}>
                <FormLabel mb={2} fontSize="0.9rem" fontWeight="600" color="#1c2963">
                  {blu_name}
                </FormLabel>
                <Input
                  type="date"
                  value={componentValues[blu_componentid] || ''}
                  onChange={(e) => handleComponentValueChange(blu_componentid, e.target.value)}
                  variant="flushed"
                  size="sm"
                  py={3}
                  bg="transparent"
                  sx={{
                    _placeholder: {
                      fontSize: '0.85rem',
                      fontWeight: 'bold',
                      color: '#aaa6ff',
                      opacity: 0.8,
                      fontStyle: 'italic'
                    },
                    borderBottomColor: '#1c2963',
                  }}
                />
                <FormErrorMessage mt={2} fontSize="sm" fontWeight="500">
                  {fieldErrors[blu_componentid]}
                </FormErrorMessage>
              </FormControl>
            </Box>
          );
        case 758670005: // Uhrzeit
          return (
            <Box 
              key={blu_componentid} 
              py={3}
            >
              <FormControl isRequired={!!comp.blu_mendatorycode} isInvalid={!!fieldErrors[blu_componentid]}>
                <FormLabel mb={2} fontSize="0.9rem" fontWeight="600" color="#1c2963">
                  {blu_name}
                </FormLabel>
                <Input
                  type="time"
                  value={componentValues[blu_componentid] || ''}
                  onChange={(e) => handleComponentValueChange(blu_componentid, e.target.value)}
                  variant="flushed"
                  size="sm"
                  py={3}
                  bg="transparent"
                  sx={{
                    _placeholder: {
                      fontSize: '0.85rem',
                      fontWeight: 'bold',
                      color: '#aaa6ff',
                      opacity: 0.8,
                      fontStyle: 'italic'
                    },
                    borderBottomColor: '#1c2963',
                  }}
                />
                <FormErrorMessage mt={2} fontSize="sm" fontWeight="500">
                  {fieldErrors[blu_componentid]}
                </FormErrorMessage>
              </FormControl>
            </Box>
          );
        case 758670006: // Textarea
          return (
            <Box 
              key={blu_componentid} 
              py={3}
            >
              <FormControl isRequired={!!comp.blu_mendatorycode} isInvalid={!!fieldErrors[blu_componentid]}>
                <FormLabel mb={2} fontSize="0.9rem" fontWeight="600" color="#1c2963">
                  {blu_name}
                </FormLabel>
                <Textarea
                  value={componentValues[blu_componentid] || ''}
                  onChange={(e) => handleComponentValueChange(blu_componentid, e.target.value)}
                  variant="flushed"
                  size="sm"
                  minHeight="240px"
                  rows={12}
                  resize="none"
                  py={3}
                  bg="transparent"
                  sx={{
                    _placeholder: {
                      fontSize: '0.85rem',
                      fontWeight: 'bold',
                      color: '#aaa6ff',
                      opacity: 0.8,
                      fontStyle: 'italic'
                    },
                    borderBottomColor: '#1c2963',
                    _focus: {
                      borderBottomColor: '#aaa6ff',
                      boxShadow: 'none',
                    }
                  }}
                />
                <FormErrorMessage mt={2} fontSize="sm" fontWeight="500">
                  {fieldErrors[blu_componentid]}
                </FormErrorMessage>
              </FormControl>
            </Box>
          );
        default:
          return null;
      }
    }

    return null;
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    
    // Validate standard fields (all required)
    if (!standardFields.lastName.trim()) {
      errors['lastName'] = 'Nachname ist erforderlich';
    }
    if (!standardFields.firstName.trim()) {
      errors['firstName'] = 'Vorname ist erforderlich';
    }
    if (!standardFields.birthDate) {
      errors['birthDate'] = 'Geburtsdatum ist erforderlich';
    }
    if (!standardFields.insuranceCardNumber.trim()) {
      errors['insuranceCardNumber'] = 'Versicherungskarte-Nr. ist erforderlich';
    }
    const phoneError = validatePhone(standardFields.phone);
    if (phoneError) {
      errors['phone'] = phoneError;
    }
    const emailError = validateEmail(standardFields.email);
    if (emailError) {
      errors['email'] = emailError;
    }
    
    // Validate dynamic components
    components.forEach((comp) => {
      if (comp.blu_mendatorycode && !componentValues[comp.blu_componentid]) {
        errors[comp.blu_componentid] = 'Pflichtfeld';
      }
    });
    
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleDynamicSubmit = async () => {
    if (!validateForm()) {
      toast({
        title: 'Fehler',
        description: 'Bitte alle Pflichtfelder ausfüllen.',
        status: 'error',
        duration: 4000,
        isClosable: true,
        position: 'top',
      });
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Prepare form data for submission
      const formData = {
        standardFields: standardFields,
        components: componentValues
      };

      // Extract account ID from config
      const accountId = config?.blu_accountid?.accountid || config?._blu_accountid_value;

      // Submit to backend
      await apiService.submitDynamicForm(formData, selectedCategory, accountId, config?.blu_contactformid);
      
      setIsSubmitted(true);
      
    } catch (error: any) {
      toast({
        title: 'Fehler beim Absenden',
        description: error.message || 'Ein Fehler ist beim Absenden des Formulars aufgetreten.',
        status: 'error',
        duration: 6000,
        isClosable: true,
        position: 'top',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <Container centerContent py={10}>
        <Alert
          status="success"
          variant="subtle"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          textAlign="center"
          height="200px"
        >
          <AlertIcon boxSize="40px" mr={0} />
          <Heading mt={4} mb={1} size="md">
            Erfolgreich gesendet
          </Heading>
          <Text>Ihr Formular wurde erfolgreich übermittelt.</Text>
          {config?.blu_accountid?.websiteurl && (
            <a href={config.blu_accountid.websiteurl} style={{ marginTop: '20px', textDecoration: 'underline' }}>
              Zurück zur Praxis
            </a>
          )}
        </Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container centerContent py={10}>
        <Spinner size="xl" color="#aaa6ff" thickness="4px" />
      </Container>
    );
  }

  if (error || !config) {
    return (
      <Container centerContent py={10}>
        <Alert status="error">
          <AlertIcon />
          {error || 'Form configuration could not be loaded.'}
        </Alert>
      </Container>
    );
  }

  return (
    <Flex 
      direction="column"
      minH="100vh"
      background="#F8F8F8"
      position="relative"
    >
      {/* Gradient background for header, personal info, and Art der Anfrage */}
      <Box
        width="100%"
        minHeight="100vh"
        background="linear-gradient(130deg, white 50%, #F8F8F8 50%)"
        sx={{
          backgroundSize: '100vw 100vh',
          backgroundRepeat: 'no-repeat'
        }}
        pb={0}
      >
        <Container maxW="4xl" py={3} px={{ base: 3, md: 6 }} bg="transparent">
          {/* Header Section with Enhanced Styling */}
          {config?.blu_accountid?.blu_companylogo && (
            <Box my={16} bg="transparent" borderRadius="xl">
              <Flex align="center" gap={4}>
                {config.blu_accountid.websiteurl ? (
                  <a
                    href={config.blu_accountid.websiteurl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ display: 'inline-block' }}
                  >
                    <Box
                      maxW="200px"
                      maxH="120px"
                      minW="80px"
                      minH="40px"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      flexShrink={0}
                      cursor="pointer"
                    >
                      <Image
                        src={config.blu_accountid.entityimageUrl || `data:image/jpeg;base64,${config.blu_accountid.blu_companylogo}`}
                        alt={config.blu_accountid.name}
                        maxW="100%"
                        maxH="100%"
                        objectFit="contain"
                        width="auto"
                        height="auto"
                      />
                    </Box>
                  </a>
                ) : (
                  <Box
                    maxW="200px"
                    maxH="120px"
                    minW="80px"
                    minH="40px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    flexShrink={0}
                  >
                    <Image
                      src={config.blu_accountid.entityimageUrl || `data:image/jpeg;base64,${config.blu_accountid.blu_companylogo}`}
                      alt={config.blu_accountid.name}
                      maxW="100%"
                      maxH="100%"
                      objectFit="contain"
                      width="auto"
                      height="auto"
                    />
                  </Box>
                )}
                <VStack align="start" spacing={2} flex="1">
                  {config.blu_accountid.name && (
                    <Heading size="lg" color="#1c2963" fontWeight="600" letterSpacing="-0.025em">
                      {config.blu_accountid.name}
                    </Heading>
                  )}
                  {config.blu_accountid.blu_descriptionforcontactform && (
                    <Box
                      className="description-content"
                      ref={(el) => {
                        if (el) {
                          const links = el.querySelectorAll('a');
                          links.forEach(link => {
                            link.setAttribute('target', '_blank');
                            link.setAttribute('rel', 'noopener noreferrer');
                          });
                        }
                      }}
                      sx={{
                        fontSize: '0.5rem',
                        '& a': {
                          color: '#1c2963',
                          textDecoration: 'underline',
                          _hover: {
                            color: '#aaa6ff',
                            textDecoration: 'underline'
                          }
                        }
                      }}
                      dangerouslySetInnerHTML={{ 
                        __html: DOMPurify.sanitize(config.blu_accountid.blu_descriptionforcontactform) 
                      }}
                    />
                  )}
                </VStack>
              </Flex>
            </Box>
          )}
          {/* Main Form Container with Enhanced Styling */}
          <Box bg="transparent" borderRadius="xl" overflow="hidden">
            {/* Personal Information Section */}
            <FieldSection mb={4} border="0" borderColor="transparent" px={8}>
              {/* Section Header */}
              <Box mb={3}>
                <Heading size="sm" color="#1c2963" fontWeight="600" mb={1} letterSpacing="-0.025em">
                  Persönliche Informationen
                </Heading>
                <Text color="gray.600" fontSize="0.85rem" fontStyle="italic">
                  Bitte füllen Sie alle erforderlichen Felder aus
                </Text>
              </Box>
              <VStack spacing={6} align="stretch">
                <Box w="full">
                  <VStack spacing={4} align="stretch">
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={12}>
                      <FormControl isRequired isInvalid={!!fieldErrors['firstName']}>
                        <FormLabel mb={1} fontSize="0.85rem" fontWeight="600" color="#1c2963">
                          Vorname
                        </FormLabel>
                        <Input 
                          type="text" 
                          value={standardFields.firstName}
                          onChange={(e) => handleStandardFieldChange('firstName', e.target.value)}
                          maxLength={50}
                          size="sm"
                          py={3}
                          variant="flushed"
                          sx={{
                            borderBottomColor: '#e0e0e0',
                            fontSize: '0.9rem'
                          }}
                        />
                        <FormErrorMessage mt={2} fontSize="sm" fontWeight="500">
                          {fieldErrors['firstName']}
                        </FormErrorMessage>
                      </FormControl>
                      <FormControl isRequired isInvalid={!!fieldErrors['lastName']}>
                        <FormLabel mb={1} fontSize="0.85rem" fontWeight="600" color="#1c2963">
                          Nachname
                        </FormLabel>
                        <Input 
                          type="text" 
                          value={standardFields.lastName}
                          onChange={(e) => handleStandardFieldChange('lastName', e.target.value)}
                          maxLength={50}
                          size="sm"
                          py={3}
                          variant="flushed"
                          sx={{
                            borderBottomColor: '#e0e0e0',
                            fontSize: '0.9rem'
                          }}
                        />
                        <FormErrorMessage mt={2} fontSize="sm" fontWeight="500">
                          {fieldErrors['lastName']}
                        </FormErrorMessage>
                      </FormControl>
                    </SimpleGrid>
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={12}>
                      <FormControl isRequired isInvalid={!!fieldErrors['birthDate']}>
                        <FormLabel mb={1} fontSize="0.85rem" fontWeight="600" color="#1c2963">
                          Geburtsdatum
                        </FormLabel>
                        <Input 
                          type="date"
                          value={standardFields.birthDate}
                          onChange={(e) => handleStandardFieldChange('birthDate', e.target.value)}
                          maxLength={50}
                          size="sm"
                          py={3}
                          variant="flushed"
                          sx={{
                            borderBottomColor: '#e0e0e0',
                            fontSize: '0.9rem'
                          }}
                        />
                        <FormErrorMessage mt={2} fontSize="sm" fontWeight="500">
                          {fieldErrors['birthDate']}
                        </FormErrorMessage>
                      </FormControl>
                      <FormControl isRequired isInvalid={!!fieldErrors['insuranceCardNumber']}>
                        <FormLabel mb={1} fontSize="0.85rem" fontWeight="600" color="#1c2963">
                          Versicherungskarte-Nr.
                        </FormLabel>
                        <Input 
                          type="text" 
                          value={standardFields.insuranceCardNumber}
                          onChange={(e) => handleStandardFieldChange('insuranceCardNumber', e.target.value)}
                          maxLength={50}
                          size="sm"
                          py={3}
                          variant="flushed"
                          sx={{
                            borderBottomColor: '#e0e0e0',
                            fontSize: '0.9rem'
                          }}
                        />
                        <FormErrorMessage mt={2} fontSize="sm" fontWeight="500">
                          {fieldErrors['insuranceCardNumber']}
                        </FormErrorMessage>
                      </FormControl>
                    </SimpleGrid>
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={12}>
                      <FormControl isRequired isInvalid={!!fieldErrors['phone']}>
                        <FormLabel mb={1} fontSize="0.85rem" fontWeight="600" color="#1c2963">
                          Telefon
                        </FormLabel>
                        <Input 
                          type="tel" 
                          value={standardFields.phone}
                          onChange={(e) => handleStandardFieldChange('phone', e.target.value)}
                          maxLength={50}
                          size="sm"
                          py={3}
                          variant="flushed"
                          sx={{
                            borderBottomColor: '#e0e0e0',
                            fontSize: '0.9rem'
                          }}
                        />
                        <FormErrorMessage mt={2} fontSize="sm" fontWeight="500">
                          {fieldErrors['phone']}
                        </FormErrorMessage>
                      </FormControl>
                      <FormControl isRequired isInvalid={!!fieldErrors['email']}>
                        <FormLabel mb={1} fontSize="0.85rem" fontWeight="600" color="#1c2963">
                          E-Mail
                        </FormLabel>
                        <Input 
                          type="email" 
                          value={standardFields.email}
                          onChange={(e) => handleStandardFieldChange('email', e.target.value)}
                          maxLength={50}
                          size="sm"
                          py={3}
                          variant="flushed"
                          sx={{
                            borderBottomColor: '#e0e0e0',
                            fontSize: '0.9rem'
                          }}
                        />
                        <FormErrorMessage mt={2} fontSize="sm" fontWeight="500">
                          {fieldErrors['email']}
                        </FormErrorMessage>
                      </FormControl>
                    </SimpleGrid>
                  </VStack>
                </Box>
              </VStack>
            </FieldSection>
            {/* Art der Anfrage Section - moved up */}
            <FieldSection bg="white" mt={16}>
              {/* Section Header */}
              <Box mb={2}>
                <Heading size="sm" color="#1c2963" fontWeight="600" mb={1} letterSpacing="-0.025em">
                  Art der Anfrage
                </Heading>
                <Text color="gray.600" fontSize="0.8rem" fontStyle="italic">
                  Wählen Sie die Kategorie für Ihre Anfrage
                </Text>
              </Box>
              <VStack spacing={3} align="stretch">
                <FormControl isRequired>
                  <Select 
                    placeholder="Bitte wählen Sie eine Kategorie" 
                    value={selectedCategory} 
                    onChange={handleCategoryChange} 
                    isDisabled={loadingComponents}
                    variant="flushed"
                    size="lg"
                    bg="transparent"
                  >
                    {config.blu_contactform_blu_category_blu_category?.map((cat: Category, idx: number) => (
                      <option key={idx} value={cat.blu_categoryid}>
                        {cat.blu_name}
                      </option>
                    ))}
                  </Select>
                </FormControl>

                {/* Enhanced Category Description */}
                {selectedCategory && (() => {
                  const selectedCat = config.blu_contactform_blu_category_blu_category?.find(
                    cat => cat.blu_categoryid === selectedCategory
                  );
                  return selectedCat?.blu_richdescription ? (
                    <Box
                      className="description-content"
                      py={2}
                      dangerouslySetInnerHTML={{ 
                        __html: DOMPurify.sanitize(selectedCat.blu_richdescription) 
                      }}
                    />
                  ) : null;
                })()}

                {/* Enhanced Loading State */}
                {loadingComponents && (
                  <Box 
                    textAlign="center" 
                    py={4}
                    bg="transparent"
                  >
                    <Spinner size="lg" color="#aaa6ff" thickness="3px" />
                    <Text mt={2} color="gray.600" fontStyle="italic">
                      Lade Formularfelder...
                    </Text>
                  </Box>
                )}

                {/* Dynamic Components */}
                {components.length > 0 && (
                  <Box bg="transparent" pt={2}>
                    <VStack spacing={1} w="full" align="stretch">
                      {components.map((comp: Component) => renderComponent(comp))}
                    </VStack>
                  </Box>
                )}

                {/* Enhanced Error Display */}
                {!!componentsError && (
                  <Alert 
                    status="error" 
                    borderRadius="lg"
                    border="1px solid"
                    borderColor="red.200"
                    bg="red.50"
                    py={2}
                  >
                    <AlertIcon />
                    <Text fontWeight="500">{(componentsError as Error).message}</Text>
                  </Alert>
                )}

                {/* Submit Button positioned below the dynamic field section card */}
                {components.length > 0 && (
                  <Box pt={2}>
                    <Button 
                      colorScheme="brand" 
                      onClick={handleDynamicSubmit}
                      isLoading={isSubmitting}
                      loadingText="Wird gesendet..."
                      size="md"
                      py={2}
                      px={6}
                      fontSize="0.95rem"
                      fontWeight="600"
                      borderRadius="lg"
                      _hover={{
                        transform: 'translateY(-1px)',
                      }}
                      _active={{
                        transform: 'translateY(0px)',
                      }}
                      transition="all 0.2s ease"
                    >
                      Absenden
                    </Button>
                  </Box>
                )}
              </VStack>
            </FieldSection>
            {/* Help Section below Art der Anfrage */}
            <Box mt={8} mb={4} px={8} py={4} bg="#F8F8F8" borderRadius="lg" border="1px solid #e0e0e0">
              <Text fontSize="0.9rem" color="#1c2963">
                Falls Sie weitere Fragen haben oder Unterstützung beim Ausfüllen des Formulars benötigen, wenden Sie sich bitte direkt an Ihre Praxis.
              </Text>
            </Box>
          </Box>
        </Container>
      </Box>
      {/* Solid grey background for the rest of the content (if any) */}
      {/* <Box bg="#F8F8F8" w="100%" flex="1" pb={0}> ... </Box> if you have more content below */}
      
      {/* Footer with gradient */}
      <Box
        as="footer"
        background="linear-gradient(131.93deg, #1c2963 28.15%, #aaa6ff)"
        py={{ base: 8, md: 12, lg: 16 }}
        px={{ base: 4, md: 8, lg: 12 }}
      >
        <Container maxW="8xl" px={0}>
          <Flex
            direction="column"
            gap={{ base: 6, md: 8 }}
            color="white"
            align="center"
            textAlign="center"
          >
            {/* Service text and logo section */}
            <Flex 
              direction={{ base: 'column', sm: 'row' }}
              align="center" 
              gap={{ base: 3, sm: 4 }}
              justify="center"
              flexWrap="wrap"
            >
              <Text 
                fontSize={{ base: 'xs', sm: 'sm' }}
                fontWeight="400" 
                color="white"
                lineHeight="1.4"
                display="flex"
                alignItems="center"
              >
                Ein Service von {config?.blu_accountid?.name || 'BlueCare'} in Zusammenarbeit mit
              </Text>
              <Image 
                src={logo} 
                alt="BlueCare Logo" 
                height={{ base: '24px', sm: '28px', md: '32px' }}
                filter="brightness(0) invert(1)"
                flexShrink={0}
                alignSelf="center"
              />
            </Flex>
            
            {/* Legal links and copyright section */}
            <Flex
              direction={{ base: 'column', sm: 'row' }}
              align="center"
              justify="center"
              gap={{ base: 4, sm: 6, md: 8 }}
              flexWrap="wrap"
            >
              {/* Legal links */}
              <HStack 
                spacing={{ base: 4, sm: 6 }} 
                fontSize={{ base: 'xs', sm: 'sm' }}
                flexWrap="wrap"
                justify="center"
              >
                <ChakraLink 
                  href="https://bluecare.ch/datenschutz/" 
                  isExternal
                  fontWeight="bold"
                  textDecoration="underline"
                  textDecorationColor="#aaa6ff"
                  _hover={{ textDecoration: 'underline', textDecorationColor: '#aaa6ff' }}
                >
                  Datenschutz
                </ChakraLink>
                <ChakraLink
                  as={RouterLink}
                  to="/impressum"
                  fontWeight="bold"
                  textDecoration="underline"
                  textDecorationColor="#aaa6ff"
                  _hover={{ textDecoration: 'underline', textDecorationColor: '#aaa6ff' }}
                >
                  Impressum
                </ChakraLink>
              </HStack>
              
              {/* Copyright */}
              <Text 
                fontSize={{ base: 'xs', sm: 'sm' }} 
                fontWeight="500" 
                color="white"
                opacity={0.9}
              >
                © 2025 by BlueCare AG
              </Text>
            </Flex>
          </Flex>
        </Container>
      </Box>
    </Flex>
  );
};

export default FormPage; 